import { getIntlCurrency, SubscriptionTier } from '@invoice-simple/common';
import { FeatureName, InvoiceCreationLimit } from '@invoice-simple/feature-gate';

import { ISIntl } from 'src/i18n';
import UserModel, { Cadence } from 'src/models/UserModel';
import { defaultsStore } from 'src/stores/DefaultsStore';
import { getURLQueryParam, URLQueryParamKeys } from 'src/util/url';
import { messages } from './messages';

export const getPaywallVariant = (
  currentTier: SubscriptionTier | null,
  tierRequired: SubscriptionTier | null
) => {
  if (currentTier === SubscriptionTier.ESSENTIALS && tierRequired === SubscriptionTier.PLUS)
    return 'essentials-to-plus';
  if (currentTier === SubscriptionTier.ESSENTIALS && tierRequired === SubscriptionTier.PREMIUM)
    return 'essentials-to-premium';
  if (currentTier === SubscriptionTier.PLUS && tierRequired === SubscriptionTier.PREMIUM)
    return 'plus-to-premium';
  return 'default';
};

/**
 * The reason the user hit the paywall.
 */
export const getReasonForUpgrade = (feature: FeatureName, user: UserModel) => {
  if (feature) return feature;
  if (user.isDocLimitedTier) {
    if (
      user.docQuota === InvoiceCreationLimit.TIER_1_LIMIT &&
      user.docsCountLastMonth >= user.docQuota
    ) {
      return '3-invoice-limit';
    }

    if (
      user.docQuota === InvoiceCreationLimit.TIER_2_LIMIT &&
      user.docsCountLastMonth >= user.docQuota
    ) {
      return '10-invoice-limit';
    }
  }

  return null;
};

export const getPeriodicity = (cadence?: Cadence) => {
  return cadence === Cadence.ANNUAL ? messages.perYear : messages.perMonth;
};

export const getIntervalText = (cadence: Cadence, ft: ISIntl['formatMessage']) => {
  const user = UserModel.getInstance();
  const currencyCode = user.geoSubCurrencyCode?.toUpperCase();

  const renderIntervalText = (cadence: Cadence, currency: string) => {
    const intervalTextMessage = getPeriodicity(cadence);
    const currenciesWithPrefix = ['CAD', 'AUD'];

    const shouldShowPrefix = currenciesWithPrefix.includes(currency);
    const prefix = shouldShowPrefix ? currency : '';
    const translatedIntervalText = ft(intervalTextMessage);
    return `${prefix}${translatedIntervalText}`;
  };

  return renderIntervalText(cadence, currencyCode);
};

export const isFreeTierPaywallEnabled = () => {
  const user = UserModel.getInstance();

  return !user.isGuest && !user.isSubPaid && !user.hadPaidSubsInThePast;
};

export const getFormattedCurrency = (user: UserModel, amount: number) => {
  return getIntlCurrency({
    number: amount,
    currencyCode: user.geoSubCurrencyCode,
    localeCode: defaultsStore.locale.name
  });
};

export const showPremiumOnlyPaywall = () => {
  const feature = getURLQueryParam(URLQueryParamKeys.FEATURE) as FeatureName;
  switch (feature) {
    case FeatureName.VERSION_HISTORY:
      return true;
    default:
      return false;
  }
};
